import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search, Heart, ChevronRight, Leaf } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api } from "@/lib/api";
import { concernVisual } from "@/lib/concerns";
import { concernImage } from "@/lib/concernImages";

export default function Concerns() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { data: concerns = [], isLoading } = useQuery({ queryKey: ["diseases"], queryFn: () => api.listDiseases() });
  const filtered = concerns.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 text-brand mb-1"><Heart size={18} /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Wellness Packages</span></div>
        <h1 className="font-display text-4xl sm:text-5xl text-ink">Shop by Concern</h1>
        <p className="text-muted2 mt-2">Curated herbal packages with recommended dosage for common health concerns.</p>

        <div className="relative max-w-md mt-6">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted2" size={18} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search concerns…" className="w-full h-12 rounded-full border border-line pl-10 pr-4 text-sm focus:outline-none focus:border-brand" data-testid="concern-search" />
        </div>

        {isLoading ? (
          <p className="text-muted2 mt-10">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted2" data-testid="concerns-empty">
            <Leaf className="mx-auto text-line" size={40} />
            <p className="mt-3">No concerns available yet.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {filtered.map((c) => {
              const { Icon, color } = concernVisual(c.name);
              const img = concernImage(c.name);
              return (
                <button key={c.id} onClick={() => navigate(`/concerns/${c.id}`)} data-testid={`concern-${c.id}`} className="text-left rounded-3xl bg-paper border border-line/70 p-4 shadow-[0_6px_24px_-10px_rgba(0,0,0,0.18)] hover:shadow-[0_16px_36px_-12px_rgba(0,0,0,0.28)] hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-4 group">
                  {img ? (
                    <img src={img} alt={c.name} loading="lazy" className="w-[76px] h-[76px] rounded-[22px] object-cover shrink-0 transition-transform duration-300 group-hover:scale-105 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.25)] ring-1 ring-black/5" />
                  ) : (
                    <div className="w-[76px] h-[76px] rounded-[22px] flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.25),inset_0_1px_2px_rgba(255,255,255,0.7)] ring-1 ring-black/5"
                      style={{ background: `linear-gradient(145deg, ${color}33, ${color}12)` }}>
                      <Icon size={38} strokeWidth={1.6} style={{ color }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-2xl text-ink group-hover:text-brand transition-colors truncate">{c.name}</h3>
                    <p className="text-sm text-muted2 mt-0.5">{c.product_count} recommended product{c.product_count === 1 ? "" : "s"}</p>
                  </div>
                  <ChevronRight className="text-muted2 group-hover:text-brand transition-colors shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
